import React, { useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import Breadcrums from "../../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import * as ClubManagamentApi from "../../../services/club.service";
import Swal from "sweetalert2";
import { handleServerValidations, formateDateWithMonth } from "../../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { Placeholder } from "rsuite";

ModuleRegistry.registerModules([AllCommunityModule]);

const BillingLogs = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const gridRef = useRef();
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Club Management"), url: "/admin/club-management/club/list/1" },
    { title: "Billing Logs", url: "" },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ["billing-logs", id],
    queryFn: () => ClubManagamentApi.getBillingLogs(id),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const colDefs = useMemo(() => [
    { 
      headerName: "ID", 
      field: "id", 
      minWidth: 150, 
      maxWidth: 250, 
      tooltipField: "id",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    { headerName: "Billing Period", field: "billingPeriod", minWidth: 150 },
    { 
      headerName: "Invoice ID", 
      field: "stripeInvoiceId", 
      minWidth: 150, 
      maxWidth: 200,
      tooltipField: "stripeInvoiceId",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    { headerName: "Player Count", field: "playerCount", minWidth: 120 },
    { headerName: "Player Additions", field: "additionalPlayersCount", valueFormatter: params => params.value !== undefined ? params.value : 0, minWidth: 130 },
    { headerName: "Adjustment Amount", field: "additionalAmount", valueFormatter: params => params.value ? `£${(params.value/100).toFixed(2)}` : "£0.00", minWidth: 150 },
    { headerName: "Total Amount", field: "totalAmount", valueFormatter: params => params.value ? `£${(params.value/100).toFixed(2)}` : "£0.00", minWidth: 120 },
    { headerName: "Discount Applied", field: "discountApplied", valueGetter: params => params.data.discountApplied ? "Yes" : "No", minWidth: 130 },
    { headerName: "Status", field: "status", minWidth: 120, cellRenderer: params => {
      const val = params.value ? params.value.toLowerCase() : "";
      let displayStatus = val;
      if (val === "flagged") {
        displayStatus = "paid";
      }
      
      let badgeClass = "badge-primary-light";
      if (displayStatus === "paid") {
        badgeClass = "badge-success-light";
      } else if (displayStatus === "failed") {
        badgeClass = "badge-danger-light";
      } else if (displayStatus === "invoiced") {
        badgeClass = "badge-info-light";
      } else if (displayStatus === "pending") {
        badgeClass = "badge-warning-light";
      }
      
      return <span className={`badge badge-pill ${badgeClass} fw-bold text-uppercase`}>{displayStatus || "N/A"}</span>;
    }},
    { headerName: "Webhook Event", field: "webhookEvent", minWidth: 180 },
    { headerName: "Resolved At", field: "resolvedAt", valueFormatter: params => params.value ? formateDateWithMonth(params.value) : "N/A", minWidth: 180 },
    {
      headerName: "Created Date",
      field: "createdAt",
      minWidth: 180,
      valueFormatter: params => params.value ? formateDateWithMonth(params.value) : "N/A"
    }
  ], []);

  const rowData = useMemo(() => {
    let list = data?.data || [];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(item => 
        (item.stripeInvoiceId && item.stripeInvoiceId.toLowerCase().includes(s)) ||
        (item.id && item.id.toLowerCase().includes(s))
      );
    }
    if (statusFilter && statusFilter !== "all") {
      list = list.filter(item => {
        let itemStatus = item.status ? item.status.toLowerCase() : "";
        if (itemStatus === "flagged") {
          itemStatus = "paid";
        }
        return itemStatus === statusFilter.toLowerCase();
      });
    }
    return list;
  }, [data, search, statusFilter]);

  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        setPerPage(params.api.paginationGetPageSize());
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
              <h6 className="main-content-label mb-0">Billing Logs</h6>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div style={{ width: 200 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Invoice ID"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>
                <div style={{ width: 150 }}>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="invoiced">Invoiced</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>
            {isLoading ? (
              <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingLogs;
